import React, { useContext, useState } from 'react';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import BoltIcon from '@mui/icons-material/Bolt';
import { useNavigate } from 'react-router-dom';
import { useAutoTranslation } from 'src/hooks/useTranslate';
import { UserContext } from 'src/store/ContextStore';
import {
  QUICK_ACTION_GROUPS,
  QUICK_ACTIONS_MENU_FLAG
} from 'src/config/quickActions';
import { isFeatureEnabled } from 'src/components/admin/home/featureGating';
import { filterQuickActionGroups } from './quickActionsMenuFilter';

const MENU_ID = 'quick-actions-menu';

const subheaderSx = {
  lineHeight: '30px',
  fontSize: 12,
  fontWeight: 600,
  color: 'text.secondary',
  bgcolor: 'transparent'
};

/**
 * Global quick-actions menu for the top bar. Icon-only trigger opening a grouped
 * dropdown driven by the QUICK_ACTION_GROUPS registry. Renders nothing unless the
 * user is authenticated and the feature flag is on. Role/feature gating is done
 * by the pure filterQuickActionGroups helper.
 *
 * @param {{ systemFeatures?: object }} props
 */
export default function QuickActionsMenu({ systemFeatures }) {
  const t = useAutoTranslation();
  const navigate = useNavigate();
  const userCtx = useContext(UserContext);
  const user = userCtx && userCtx.user;
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  // Self-gate: authenticated clinician/admin AND the menu flag is on.
  if (!user || !isFeatureEnabled(QUICK_ACTIONS_MENU_FLAG, systemFeatures)) {
    return null;
  }

  const groups = filterQuickActionGroups(
    QUICK_ACTION_GROUPS,
    user.userType,
    systemFeatures
  );
  if (groups.length === 0) return null;

  const label = t('Quick actions');

  const handleOpen = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const handleSelect = (action) => {
    handleClose();
    if (action.path) navigate(action.path);
  };

  // MUI Menu does not accept a Fragment child, so build a flat array of items:
  // [subheader, item, item, divider, subheader, item, ...]. Non-focusable
  // subheaders/dividers are skipped by MenuList's arrow-key navigation.
  const items = [];
  groups.forEach((group, index) => {
    if (index > 0) {
      items.push(<Divider key={`divider-${group.id}`} component="li" />);
    }
    items.push(
      <ListSubheader key={`group-${group.id}`} disableSticky sx={subheaderSx}>
        {t(group.label)}
      </ListSubheader>
    );
    group.actions.forEach((action) => {
      const Icon = action.Icon;
      items.push(
        <MenuItem
          key={action.key}
          onClick={() => handleSelect(action)}
          data-testid={`quick-action-${action.key}`}
        >
          {Icon && (
            <ListItemIcon>
              {/* Colour each icon to match its equivalent home-page tile. */}
              <Icon fontSize="small" sx={{ color: action.iconColor }} />
            </ListItemIcon>
          )}
          <ListItemText>{t(action.label)}</ListItemText>
        </MenuItem>
      );
    });
  });

  return (
    <>
      <Tooltip title={label}>
        <IconButton
          color="inherit"
          size="large"
          onClick={handleOpen}
          aria-label={label}
          aria-haspopup="menu"
          aria-controls={open ? MENU_ID : undefined}
          aria-expanded={open ? 'true' : undefined}
          data-testid="quick-actions-trigger"
          data-va-anchor="topbar.quickActions"
        >
          <BoltIcon />
        </IconButton>
      </Tooltip>
      <Menu
        id={MENU_ID}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        MenuListProps={{ 'aria-label': label, dense: true }}
      >
        {items}
      </Menu>
    </>
  );
}
